// The case stream. EventSource cannot carry the bearer header, so this reads
// text/event-stream over fetch and parses it. Reconnects with backoff; the
// caller de-duplicates by case id.
import type { Case } from "@artemis-group/aiw-contracts";
import { authHeaders, fetchImpl } from "./api";
import { env } from "./env";

export type CaseListener = (k: Case) => void;

export function subscribeCases(onCase: CaseListener, onStatus?: (s: "open" | "closed" | "error") => void): () => void {
  const ctrl = new AbortController();
  let backoff = 1000;
  let stopped = false;

  async function run() {
    while (!stopped) {
      try {
        const res = await fetchImpl(`${env.apiBase}/api/cases/stream?surface=crm`, { headers: await authHeaders(), signal: ctrl.signal });
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        onStatus?.("open");
        backoff = 1000;
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let idx: number;
          while ((idx = buf.indexOf("\n\n")) >= 0) {
            const frame = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let event = "message";
            const data: string[] = [];
            for (const line of frame.split("\n")) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
            }
            if (event === "case" && data.length) {
              try { onCase(JSON.parse(data.join("\n")) as Case); } catch { /* skip a bad frame */ }
            }
          }
        }
        onStatus?.("closed");
      } catch (e) {
        if (stopped || (e as Error).name === "AbortError") return;
        onStatus?.("error");
      }
      await new Promise((r) => setTimeout(r, backoff));
      backoff = Math.min(backoff * 2, 30_000);
    }
  }
  void run();
  return () => { stopped = true; ctrl.abort(); };
}
